/**
 * Collection-native property discovery + range computation for the hexagon
 * color-filter — the counterpart to the model's `useAvailableProperties` /
 * `usePerBandPropertyRanges`, but pure and operating over our flat `cell.stats`
 * instead of OpenLayers feature internals (`feature.values_.data.metadata`).
 *
 * Reuses the shared `AVAILABLE_PROPERTIES` catalog and `getPropertyValue` so the
 * property ids and semantics stay identical to the model classifier. Labels are
 * the one exception: a small collection-only override map (`COLLECTION_LABELS`)
 * corrects entries whose collection value means something different from the
 * model's. The shared catalog is read-only here and is never edited or mutated.
 *
 * @module hexagonClassificationProperties
 */

import {
	AVAILABLE_PROPERTIES,
	getPropertyValue,
} from '@routes/userRoutes/projects/singleProject/components/modifyLayers/hexagonColorFilter/matrixFilter/matrixClassification';

import { objectLabel } from './transformUtils';

const EMPTY_RANGE = { min: 0, max: 100, hasData: false };

// Collection-only label overrides for shared catalog entries whose collection
// semantics differ from the model's.
//
// `total_area` resolves to the summed per-class `sum_object_area` on collection
// cells (deriveCellStats exposes it), so the model's "Total Area (m²)" would be
// a false label here. That model label describes the container only on the model
// hexagon path, which never sets `total_area` and so falls through the
// resolver's `?? meta.area`; model DSP sets it itself — when the plot has
// detections — as the summed per-detection `area` (`useDspEnrichment`), object
// area too. So the same quantity reads "Total Object Area (m²)" here and
// "Total Area (m²)" in the model DSP filter — a pre-existing inconsistency, out
// of scope: the rename is collection-side only and the shared catalog is not
// edited or mutated.
const COLLECTION_LABELS = { total_area: 'Total Object Area (m²)' };

/**
 * Apply the collection label overrides, mapping to new objects so a shared
 * `AVAILABLE_PROPERTIES` entry is never mutated in place.
 *
 * Must be applied on EVERY return path of `getAvailableProperties` — otherwise
 * the dropdown text flips depending on whether the collection happens to carry
 * dynamic per-class stem keys.
 */
const relabelForCollection = props =>
	props.map(p => (COLLECTION_LABELS[p.id] ? { ...p, label: COLLECTION_LABELS[p.id] } : p));

// Dynamic per-object enriched keys (e.g. "stems_low:<node uuid>",
// "stems_per_ha_low:<node uuid>", "hectare_equivalent:<node uuid>"). The label
// names the object by the collection's name for it, never the raw uuid.
// Order matters: the more specific prefix must be tested first.
//
// `hasLevel` distinguishes the two key shapes. Stem keys embed a Low/Medium/High
// level between the prefix and the object ("stems_low:<node uuid>"); the hectare
// equivalent has the colon in its prefix, so everything after it is the node
// uuid ("hectare_equivalent:<node uuid>"). Same split the model path's
// useAvailableProperties makes with its `requiresColon` flag.
const DYNAMIC_KEY_PATTERNS = [
	{
		prefix: 'hectare_equivalent:',
		hasLevel: false,
		getLabel: cls => `Ha/eq (${cls})`,
		unit: '/ha',
	},
	{
		prefix: 'stems_per_ha_',
		hasLevel: true,
		getLabel: (cls, lvl) => `Stems/ha ${lvl} (${cls})`,
		unit: '/ha',
	},
	{
		prefix: 'stems_',
		excludePrefix: 'stems_per_ha_',
		hasLevel: true,
		getLabel: (cls, lvl) => `Stems ${lvl} (${cls})`,
		unit: 'pcs',
	},
];

/**
 * Parse a stats key as a dynamic per-object property, or null if it isn't one.
 * `nameOf` turns the node uuid in the key into the object's display name.
 */
const parseDynamicKey = (key, nameOf) => {
	if (!key.includes(':')) return null;
	for (const pattern of DYNAMIC_KEY_PATTERNS) {
		if (!key.startsWith(pattern.prefix)) continue;
		if (pattern.excludePrefix && key.startsWith(pattern.excludePrefix)) continue;
		const rest = key.slice(pattern.prefix.length);
		if (!pattern.hasLevel) {
			return { id: key, label: pattern.getLabel(nameOf(rest)), unit: pattern.unit };
		}
		const colonIdx = rest.indexOf(':');
		const level = rest.slice(0, colonIdx);
		const nodeUuid = rest.slice(colonIdx + 1);
		const levelLabel = level.charAt(0).toUpperCase() + level.slice(1);
		return {
			id: key,
			label: pattern.getLabel(nameOf(nodeUuid), levelLabel),
			unit: pattern.unit,
		};
	}
	return null;
};

/**
 * The properties that actually have data across the given cells — the catalog
 * entries whose value resolves for at least one cell, plus any dynamic per-class
 * stem properties present. When no cell has data (or none provided) the full
 * catalog is returned so the editor still offers options.
 *
 * Every return path runs the result through `relabelForCollection`, so the
 * collection labels do not depend on which branch a given collection takes.
 *
 * @param {import('./collectionHexagonsToCells').HexagonCell[]} cells
 * @param {Array<{key: string, label: string}>} [objects] - the collection's objects, to
 *   name the per-object properties
 * @returns {Array<{ id: string, label: string, unit: string }>}
 */
export const getAvailableProperties = (cells, objects) => {
	const nameOf = nodeUuid => objectLabel(objects, nodeUuid);
	if (!cells?.length) return relabelForCollection(AVAILABLE_PROPERTIES);

	const foundIds = new Set();
	const dynamic = [];
	const foundDynamic = new Set();

	for (const cell of cells) {
		const meta = cell.stats;
		if (!meta) continue;

		for (const prop of AVAILABLE_PROPERTIES) {
			if (foundIds.has(prop.id)) continue;
			const value = getPropertyValue(meta, prop.id);
			if (value !== null && !Number.isNaN(value)) foundIds.add(prop.id);
		}

		for (const key of Object.keys(meta)) {
			if (foundDynamic.has(key)) continue;
			const value = meta[key];
			if (value == null || Number.isNaN(value)) continue;
			const parsed = parseDynamicKey(key, nameOf);
			if (parsed) {
				dynamic.push(parsed);
				foundDynamic.add(key);
			}
		}
	}

	// Relabelled here so both remaining return paths inherit it — the
	// no-resolved-properties branch and the bare `return staticResult` below, plus
	// the dynamic branch that maps over it.
	const staticResult = relabelForCollection(
		foundIds.size === 0
			? AVAILABLE_PROPERTIES
			: AVAILABLE_PROPERTIES.filter(p => foundIds.has(p.id))
	);

	if (dynamic.length) {
		dynamic.sort((a, b) => a.label.localeCompare(b.label));
		// Distinguish the aggregate hectare-equivalent from per-class ones. Stays
		// conditional on dynamic per-class entries existing, unlike the
		// unconditional collection labels above.
		const relabeled = staticResult.map(p =>
			p.id === 'hectare_equivalent' ? { ...p, label: 'Ha/eq (Total)' } : p
		);
		return [...relabeled, ...dynamic];
	}

	return staticResult;
};

/**
 * Observed min/max of a property across all cells (floored/ceiled to whole
 * numbers, like the model), used for editor slider bounds and coverage-gap
 * detection. `hasData: false` when the property resolves for no cell.
 *
 * @param {import('./collectionHexagonsToCells').HexagonCell[]} cells
 * @param {string|null} propertyId
 * @returns {{ min: number, max: number, hasData: boolean }}
 */
export const getPropertyRange = (cells, propertyId) => {
	if (!cells?.length || !propertyId) return EMPTY_RANGE;

	let min = Infinity;
	let max = -Infinity;
	for (const cell of cells) {
		const value = getPropertyValue(cell.stats, propertyId);
		if (value === null || Number.isNaN(value)) continue;
		if (value < min) min = value;
		if (value > max) max = value;
	}

	if (min === Infinity) return EMPTY_RANGE;
	return { min: Math.floor(min), max: Math.ceil(max), hasData: true };
};

/**
 * Per-band observed threshold ranges — each cell assigned to its nearest band
 * (same nearest-neighbor rule as classifyWithMatrixFilter), then the threshold
 * property's min/max collected per band. Feeds getMatrixFilterCoverageGaps so
 * per-band ceilings are compared against per-band observed data.
 *
 * @param {import('./collectionHexagonsToCells').HexagonCell[]} cells
 * @param {string|null} bandProperty
 * @param {string|null} thresholdProperty
 * @param {number[]} bands
 * @returns {Array<{ min: number, max: number, hasData: boolean }>} one per band
 */
export const getPerBandThresholdRanges = (cells, bandProperty, thresholdProperty, bands) => {
	if (!cells?.length || !bandProperty || !thresholdProperty || !bands?.length) {
		return bands?.map(() => EMPTY_RANGE) ?? [];
	}

	const acc = bands.map(() => ({ min: Infinity, max: -Infinity }));

	for (const cell of cells) {
		const bandValue = getPropertyValue(cell.stats, bandProperty);
		const thresholdValue = getPropertyValue(cell.stats, thresholdProperty);
		if (bandValue === null || Number.isNaN(bandValue)) continue;
		if (thresholdValue === null || Number.isNaN(thresholdValue)) continue;

		let bandIndex = 0;
		let minDiff = Math.abs(bandValue - bands[0]);
		for (let i = 1; i < bands.length; i++) {
			const diff = Math.abs(bandValue - bands[i]);
			if (diff < minDiff) {
				minDiff = diff;
				bandIndex = i;
			}
		}

		const entry = acc[bandIndex];
		if (thresholdValue < entry.min) entry.min = thresholdValue;
		if (thresholdValue > entry.max) entry.max = thresholdValue;
	}

	return acc.map(entry =>
		entry.min === Infinity ? EMPTY_RANGE : { min: entry.min, max: entry.max, hasData: true }
	);
};
